import { identityApi } from './identityApi'
import {
  SendPhoneVerificationCodePayload,
  SendPhoneVerificationCodeDTO,
  VerifyPhoneUpdatePayload,
  VerifyPhoneUpdateDTO,
  ResendVerificationEmailPayload,
  UserProfile,
  UpdateProfilePayload,
  UpdateProfileDTO,
  RelationshipParams,
  IdentityVerificationDTO,
  UploadAvatarPayload,
  UploadAvatarDTO,
  UpdatePasswordPayload,
  ReportUserPayload,
  ReportUserDTO,
  GetBlockedUserListPayload,
  GetBlockedUserListDTO,
  CardIssueDTO,
  UpdateUserKycInfoPayload,
  UpdateUserKycInfoDTO,
  GetKycSdkAccessTokenDTO,
  GetDinariKycStatusDTO,
} from './identityTypes'
import { ApiTransaction, Transaction } from '@/domain/transactions'

/**
 * Transform API transaction to domain transaction
 */
function transformApiTransaction(apiTransaction: ApiTransaction): Transaction {
  return {
    ...apiTransaction,
    created_at: new Date(apiTransaction.created_at * 1000),
    updated_at: new Date(apiTransaction.updated_at * 1000),
  }
}

// ======== PHONE MANAGEMENT ==============
async function sendPhoneVerificationCode(
  payload: SendPhoneVerificationCodePayload,
): Promise<SendPhoneVerificationCodeDTO> {
  return identityApi.sendPhoneVerificationCode(payload)
}

async function verifyPhoneUpdate(
  payload: VerifyPhoneUpdatePayload,
): Promise<VerifyPhoneUpdateDTO> {
  return identityApi.verifyPhoneUpdate(payload)
}

// ======== USER PROFILE ==============
async function getMe(): Promise<UserProfile> {
  return identityApi.getMe()
}

async function getUserProfile(userId: string): Promise<UserProfile> {
  const profile = await identityApi.getUserProfile(userId)

  // Adapt profile data (same as in RN project)
  return {
    ...profile,
    // Transform transactions from API format to domain format
    transactions: profile.transactions?.map(transformApiTransaction) || [],
  }
}

// ======== PROFILE MANAGEMENT ==============
async function updateProfile(
  payload: UpdateProfilePayload,
): Promise<UpdateProfileDTO> {
  return identityApi.updateProfile(payload)
}

async function updateUserKycInfo(
  payload: UpdateUserKycInfoPayload,
): Promise<UpdateUserKycInfoDTO> {
  return identityApi.updateUserKycInfo(payload)
}

// ======== AVATAR UPLOAD ==============
async function uploadAvatar(
  payload: UploadAvatarPayload,
): Promise<UploadAvatarDTO> {
  return identityApi.uploadAvatar(payload)
}

// ======== RELATIONSHIP MANAGEMENT ==============
async function addUserToFavorites(params: RelationshipParams): Promise<void> {
  return identityApi.addUserToFavorites(params)
}

async function unfavoriteUser(params: RelationshipParams): Promise<void> {
  return identityApi.unfavoriteUser(params)
}

// ======== IDENTITY VERIFICATION ==============
async function identityVerification(): Promise<IdentityVerificationDTO> {
  return identityApi.identityVerification()
}

async function getKycSdkAccessToken(): Promise<GetKycSdkAccessTokenDTO> {
  return identityApi.getKycSdkAccessToken()
}

// ======== PASSWORD UPDATE ==============
async function updatePassword(payload: UpdatePasswordPayload): Promise<void> {
  return identityApi.updatePassword(payload)
}

// ======== BLOCK/UNBLOCK USERS ==============
async function blockUser(params: RelationshipParams): Promise<void> {
  return identityApi.blockUser(params)
}

async function unblockUser(params: RelationshipParams): Promise<void> {
  return identityApi.unblockUser(params)
}

// ======== REPORT USER ==============
async function reportUser(payload: ReportUserPayload): Promise<void> {
  return identityApi.reportUser(payload)
}

// ======== BLOCKED USERS ==============
async function getBlockedUserList(
  payload: GetBlockedUserListPayload,
): Promise<GetBlockedUserListDTO> {
  return identityApi.getBlockedUserList(payload)
}

// ======== EMAIL VERIFICATION ==============
async function resendVerificationEmail(
  payload: ResendVerificationEmailPayload,
): Promise<void> {
  return identityApi.resendVerificationEmail(payload)
}

// ======== DINARI KYC ==============
async function getDinariKycStatus(): Promise<GetDinariKycStatusDTO> {
  return identityApi.getDinariKycStatus()
}

async function startDinariKyc(): Promise<GetDinariKycStatusDTO> {
  return identityApi.startDinariKyc()
}

// ======== CREATE KEM CARD ==============
async function createKemCard(): Promise<CardIssueDTO> {
  return identityApi.createKemCard()
}

// ======== REQUEST USER DATA ==============
async function requestYourData(): Promise<void> {
  return identityApi.requestYourData()
}

export const identityServices = {
  sendPhoneVerificationCode,
  verifyPhoneUpdate,
  getMe,
  getUserProfile,
  updateProfile,
  updateUserKycInfo,
  uploadAvatar,
  addUserToFavorites,
  unfavoriteUser,
  blockUser,
  unblockUser,
  reportUser,
  identityVerification,
  getKycSdkAccessToken,
  updatePassword,
  getBlockedUserList,
  resendVerificationEmail,
  createKemCard,
  getDinariKycStatus,
  startDinariKyc,
  requestYourData,
}
